'use client'

// eslint-disable-next-line no-restricted-imports -- grandfathered; prefer next/image for new code
import { Box, Card, Flex, Image, Skeleton, Text, Title } from '@mantine/core'
import { useIsMobile } from '@/lib/mobileDetector'
import { ServiceItem } from '@/types/interfaces/service-item'
import { ServiceItemCardBadge } from '@/components/ServiceItemCardBadge'
import Link from 'next/link'
import GradientSvgIcon from './GradientSvgIcon'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import { PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { useBreakpoint } from '@/hooks/useBreakpoints'

interface SpecialistCardProps {
    service: ServiceItem
    showDescription?: boolean
    solo?: boolean
    pageLocation: PageLocation
    additionalInformation?: Record<string, any>
}

const SpecialistCard: React.FC<SpecialistCardProps> = ({
    service,
    showDescription = false,
    solo = false,
    pageLocation,
    additionalInformation,
}) => {
    const { isSm: isMobile } = useBreakpoint()
    const isWebAndSolo = solo && !isMobile
    const textColor = isWebAndSolo ? 'var(--Foreground-fg-primary, #1F1E1D)' : '#E8E5E3'
    const shouldShowSubtitle = service.category === ServiceItemCategory.specialty

    return (
        <Link
            href={`/s/${service.skuName}/${service.skuId}`}
            data-testid={`specialist-card-${service.skuName}`}
            onClick={() => {
                trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
                    location: pageLocation,
                    additionalInformation,
                    sku: service,
                })
            }}
        >
            <Card padding={0} display={'flex'} h={'100%'} bd="none" bg={'transparent'}>
                {isWebAndSolo ? renderSoloWebContainer() : renderContainer()}
            </Card>
        </Link>
    )

    function renderSoloWebContainer() {
        return (
            <Flex
                bg="var(--gm-background-bg-card)"
                p={24}
                gap={32}
                pos="relative"
                h={'350px'}
                w={'100%'}
                style={{
                    borderRadius: 'var(--spacing-16, 16px)',
                    overflow: 'hidden',
                }}
            >
                <Flex w={'300px'} h={'100%'} my={'auto'}>
                    {renderImage()}
                </Flex>
                <Flex flex={1} direction="column">
                    {renderInfoBoxBadge()}
                    <Flex flex={'auto'} direction="column" gap={16} justify={'center'}>
                        {renderInfoBoxTitle()}
                        {shouldShowSubtitle && renderInfoBoxSubtitle()}
                        {renderInfoBoxCost()}
                    </Flex>
                </Flex>
            </Flex>
        )
    }

    function renderContainer() {
        return (
            <Box
                pos="relative"
                h={solo ? '450px' : '350px'}
                miw={solo ? '100%' : '250px'}
                style={{
                    borderRadius: 'var(--spacing-16, 16px)',
                    overflow: 'hidden',
                }}
                bg="var(--gm-background-bg-card)"
            >
                {renderImage()}
                <Flex
                    pos="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    direction={'column'}
                    justify={'space-between'}
                    p={24}
                    bg={'linear-gradient(180deg, rgba(0, 0, 0, 0.19) 0%, rgba(0, 0, 0, 0.75) 100%)'}
                >
                    {renderInfoBoxBadge()}
                    {renderInfoBox()}
                </Flex>
            </Box>
        )
    }

    function renderImage() {
        const isConditionImage = typeof service?.imageUrl === 'string' && service.imageUrl.includes('/condition/')

        const isFallbackIcon = typeof service?.imageUrl === 'string' && service.imageUrl.startsWith('fallback-icons/')
        const creamColor = '#EBDACE'

        if (isFallbackIcon) {
            const wrapperStyle = {
                background: creamColor,
                borderRadius: isWebAndSolo ? '16px' : 0,
                width: isWebAndSolo ? '300px' : '100%',
                height: isWebAndSolo ? '300px' : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            } as const

            return (
                <Box style={wrapperStyle}>
                    <GradientSvgIcon
                        src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${service.imageUrl}`}
                        size={isWebAndSolo ? 200 : 85}
                    />
                </Box>
            )
        }

        if (isConditionImage) {
            return (
                <Image
                    src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${service?.imageUrl}`}
                    fallbackSrc={service?.image?.src}
                    alt={service.title}
                    height={isWebAndSolo ? '300px' : '100%'}
                    width={isWebAndSolo ? '300px' : '100%'}
                    fit="contain"
                    style={{
                        objectFit: 'contain',
                        objectPosition: 'center',
                        width: isWebAndSolo ? 300 : '100%',
                        height: isWebAndSolo ? 300 : '100%',
                    }}
                />
            )
        }

        return (
            <Image
                src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${service?.imageUrl}`}
                fallbackSrc={service?.image?.src}
                alt={service.title}
                height={isWebAndSolo ? '300px' : '100%'}
                width={isWebAndSolo ? '300px' : '100%'}
                radius={isWebAndSolo ? 'var(--spacing-16, 16px)' : ''}
                bg={creamColor}
            />
        )
    }

    function renderInfoBox() {
        return (
            <Flex direction="column" gap={16}>
                {renderInfoBoxTitle()}
                {(solo || showDescription) && shouldShowSubtitle && renderInfoBoxSubtitle()}
                {renderInfoBoxCost()}
            </Flex>
        )
    }

    function renderInfoBoxTitle() {
        return (
            <Title order={3} size={isWebAndSolo ? '36px' : '24px'} fw={450} c={textColor}>
                {service.title}
            </Title>
        )
    }

    function renderInfoBoxSubtitle() {
        return (
            <Text size="18px" fw={500} c={textColor}>
                Get a specialist opinion in days, not months.
            </Text>
        )
    }

    function renderInfoBoxCost() {
        return service.primaryPrice === '' ? (
            <Skeleton height={'18px'} w={'75%'} radius={'xs'} opacity={0.5} />
        ) : (
            <Flex gap={4} direction="column">
                <Text size="16px" fw={500} c={textColor}>
                    {service.primaryPrice}*
                </Text>

                <Text size="16px" c={textColor} fw={500} opacity={0.6}>
                    {service.secondaryPrice}
                </Text>
            </Flex>
        )
    }

    function renderInfoBoxBadge() {
        return <ServiceItemCardBadge category={service?.category} h={28} bg={'#F5F1ED'} iconSize={20} />
    }
}

export default SpecialistCard
